"use client";

/**
 * LoginForm — public sign-in screen for the auth route.
 *
 * Controlled fields live in Redux (`authSlice`); submission goes through
 * `useAuth().handleLogin` → `loginAction`. Email is validated client-side
 * before the server action runs.
 */

import { motion } from "framer-motion";
import { Eye, EyeOff } from "lucide-react";
import Image from "next/image";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import logo from "@/assets/images/cashless-logo.png";
import ResetPasswordModal from "@/components/forgot-password/ResetPasswordModal";
import { Label } from "@/components/ui/label";
import { LOCAL_STORE_NAME_CONFIG } from "@/config";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import CButton from "@/Shared/CButton/CButton";
import CCheckbox from "@/Shared/CCheckbox/CCheckbox";
import CInput from "@/Shared/CInput/CInput";
import {
  setEmail,
  setPassword,
} from "@/store/Features/states/auth-management/authSlice";
import { useAppSelector } from "@/store/hooks";
import isValidEmail, { INVALID_EMAIL_MESSAGE } from "@/utils/isValidEmail";

/** Tinted field styling shared across the login form inputs. */
const LOGIN_FIELD_CLASS =
  "h-auto py-3.5 bg-primary-50/40 border-2 border-transparent shadow-none focus-visible:border-primary-600 focus-visible:ring-primary-600/20";

export interface LoginFormProps extends React.ComponentProps<"div"> {}

/**
 * Animated login card — email/password fields, remember-me, forgot-password modal.
 */
const LoginForm = ({ className, ...props }: LoginFormProps) => {
  const { email, password, handleLogin, dispatch } = useAuth();
  const { isLoading } = useAppSelector((state) => state.auth);

  const [isForgotOpen, setIsForgotOpen] = useState(false);
  const [remember, setRemember] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  // Restore a previously saved email once on mount — not on every keystroke.
  useEffect(() => {
    try {
      const saved = localStorage.getItem(
        LOCAL_STORE_NAME_CONFIG.REMEMBER_EMAIL,
      );
      const trimmed = saved?.trim();
      if (trimmed) {
        dispatch(setEmail(trimmed));
        setRemember(true);
      }
    } catch {}
  }, [dispatch]);

  /** Validate trimmed email and surface an inline error when invalid. */
  const validateEmail = useCallback((value: string): boolean => {
    const trimmed = value.trim();
    // Reject before loginAction — same rules as server-side guard.
    if (!isValidEmail(trimmed)) {
      setEmailError(INVALID_EMAIL_MESSAGE);
      return false;
    }
    setEmailError(null);
    return true;
  }, []);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Block submit when format is invalid; server action validates again.
    if (!validateEmail(email)) return;
    handleLogin(remember);
  };

  return (
    <div
      className={cn("flex items-center justify-center p-4 rounded", className)}
      {...props}
    >
      <motion.div
        className="w-full max-w-md"
        initial={{ x: "calc(50% - 2px)" }}
        animate={{ x: 0 }}
        transition={{
          duration: 0.75,
          delay: 0.3,
          ease: [0.25, 0.46, 0.45, 0.94],
        }}
      >
        <div className="relative flex">
          <div className="w-1 shrink-0 rounded bg-primary-600 z-10" />

          <div className="overflow-hidden flex-1">
            <motion.div
              className="pl-10"
              initial={{ x: "calc(-100% - 32px)" }}
              animate={{ x: 0 }}
              transition={{
                duration: 0.75,
                delay: 0.3,
                ease: [0.25, 0.46, 0.45, 0.94],
              }}
            >
              <div className="mb-5">
                {/* `unoptimized`: the logo is 14KB of flat colour, and Next's
                    default WebP re-encode at quality 75 fringes its hard edges.
                    Serving the PNG as-is is both smaller and pixel-exact. The
                    intrinsic size is the asset's own, so the `scale-[1.2]`
                    transform (which Next cannot see) still paints from full
                    resolution on any display density. */}
                <Image
                  src={logo}
                  alt="Cashless Ai"
                  width={1722}
                  height={282}
                  className="block w-[350px] h-auto object-contain scale-[1.2] ml-1"
                  priority
                  unoptimized
                />
              </div>

              <div className="mb-10">
                <h2 className="font-goldman text-3xl font-bold text-foreground mb-2">
                  Welcome Back
                </h2>
                <p className="paragraph-sm text-text-tertiary">
                  Sign in to your account
                </p>
              </div>

              <form onSubmit={onSubmit} noValidate className="space-y-5">
                <CInput
                  id="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    // Clear stale error as the user edits.
                    setEmailError(null);
                    dispatch(setEmail(e.target.value));
                  }}
                  onBlur={() => {
                    // Early feedback on tab-out when the field is non-empty.
                    if (email.trim()) validateEmail(email);
                  }}
                  placeholder="Email"
                  autoComplete="email"
                  required
                  error={emailError ?? undefined}
                  className={LOGIN_FIELD_CLASS}
                />

                <CInput
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => dispatch(setPassword(e.target.value))}
                  placeholder="Password"
                  autoComplete="current-password"
                  required
                  className={LOGIN_FIELD_CLASS}
                  rightAddon={
                    <CButton
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => setShowPassword((s) => !s)}
                      className="size-7 text-text-tertiary hover:text-foreground"
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                    >
                      {showPassword ? (
                        <EyeOff className="size-4" />
                      ) : (
                        <Eye className="size-4" />
                      )}
                    </CButton>
                  }
                />

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CCheckbox
                      id="remember-me"
                      checked={remember}
                      onCheckedChange={(checked) =>
                        setRemember(checked === true)
                      }
                    />
                    <Label
                      htmlFor="remember-me"
                      className="label-sm text-text-secondary cursor-pointer select-none"
                    >
                      Remember me
                    </Label>
                  </div>

                  <CButton
                    type="button"
                    variant="link"
                    size="sm"
                    onClick={() => setIsForgotOpen(true)}
                    className="label-sm text-primary-600 h-auto p-0"
                  >
                    Forgot Password?
                  </CButton>
                </div>

                <div className="pt-6">
                  <CButton
                    type="submit"
                    variant="outline"
                    size="lg"
                    disabled={isLoading}
                    className="w-[70%] mx-auto block border-primary-600/40 text-primary-600 hover:bg-primary-600 hover:text-white hover:border-primary-600"
                  >
                    {isLoading ? "Logging in..." : "Login"}
                  </CButton>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </motion.div>

      <ResetPasswordModal
        isOpen={isForgotOpen}
        onClose={() => setIsForgotOpen(false)}
        initialEmail={email}
      />
    </div>
  );
};

export default LoginForm;
export { LoginForm };
